import type * as React from 'react';

import { Box, Tooltip, styled, type TooltipProps } from '@mui/material';

import * as infoIcon from '~/assets/icons/icon_info.svg';
import { colors, fontWeight } from '~/styles/constants';

export type LabelPosition = 'bottom' | 'left' | 'right' | 'top';

export interface LabelProps {
  children?: React.ReactNode;
  className?: string;
  dataAnalytics?: string;
  disabled?: boolean;
  htmlFor?: string;
  labelPosition?: LabelPosition;
  multiline?: boolean;
  required?: boolean;
  tooltipPosition?: TooltipProps['placement'];
  tooltipText?: string;
}

const StyledLabel = styled('label')<LabelProps>`
  margin-bottom: ${({ labelPosition }) => (labelPosition !== 'top' ? '0' : '0.25rem')};
  ${({ labelPosition }) =>
    labelPosition === 'left'
      ? `margin-right: .625rem;`
      : labelPosition === 'right'
      ? `margin-left: .625rem;`
      : labelPosition === 'bottom'
      ? `margin-top: .625rem;`
      : undefined}
  color: ${({ disabled }) => (disabled ? colors.form.disabledLabelFontColor : colors.gray700)};
  font-size: 0.875rem;
  line-height: 1.5rem;
  font-weight: ${fontWeight.normal};
  display: inline-flex;
  height: auto;
`;

const Red = styled('span')`
  color: ${colors.dangerRed};
`;

export const RedAsterisk = () => <Red>*</Red>;

const Icon = styled('img')`
  vertical-align: middle;
`;

const Label = (props: LabelProps) => {
  const label = (
    <StyledLabel
      className={props.className}
      data-analytics={props.dataAnalytics && `${props.dataAnalytics}Label`}
      disabled={props.disabled}
      htmlFor={props.htmlFor}
      labelPosition={props.labelPosition || 'top'}
    >
      {props.children} {props.required ? <RedAsterisk /> : null}{' '}
      {props.tooltipText && (
        <Tooltip
          componentsProps={{ tooltip: { sx: { width: '12rem' } } }}
          placement={(props.tooltipPosition || 'top') as TooltipProps['placement']}
          title={props.tooltipText}
        >
          <Box ml={0.5}>
            <Icon alt="" src={infoIcon} />
          </Box>
        </Tooltip>
      )}
    </StyledLabel>
  );

  return <div>{label}</div>;
};

export default Label;
